/**
 * @pt/copy — the ONE copy catalogue.
 *
 * OWNER: ux-writer (content, in design/copy*.json); web-frontend-engineer (the
 * mechanism). Read-only to backend, mobile, 3D and fabric engineers — they
 * reference keys.
 *
 * D-025 boundary 2: web and native render the same words from the same keys.
 * A re-authored string on either platform is a defect, and surfacing one is the
 * point of the native harness, not a failure of it.
 *
 * Three properties this module guarantees:
 *   1. A key outside the catalogue is a COMPILE error (`CopyKey`).
 *   2. A missing key at runtime THROWS. It never falls back to the key name and
 *      it never falls back to English-in-a-component.
 *   3. Interpolation slots are TYPED per key (`CopySlots`), and an unfilled slot
 *      throws rather than rendering `{tailorName}` to a client.
 */

import catalogue from './catalogue.json' with { type: 'json' };
import { COPY_KEY_LIST, type CopyKey, type CopySlots, type SlotValue } from './keys.ts';

export type { CopyKey, CopySlots, SlotValue };

export type Locale = 'en-GB';

const STRINGS = (catalogue as Record<string, unknown>)['en-GB'] as Record<string, string>;

/** No slots to fill ⇒ no second argument may be passed. */
type SlotArgs<K extends CopyKey> = keyof CopySlots[K] extends never ? [] : [CopySlots[K]];

function resolve(key: string, vars: Record<string, SlotValue>): string {
  const template = STRINGS[key];
  if (template === undefined) {
    throw new Error(
      `Missing copy key "${key}". Add it to design/copy*.json and rebuild — never inline the string.`,
    );
  }
  return template.replace(/\{(\w+)\}/g, (_match, name: string) => {
    if (!Object.prototype.hasOwnProperty.call(vars, name)) {
      throw new Error(
        `Copy key "${key}" needs the slot "${name}". An unfilled slot must never reach a screen.`,
      );
    }
    return String(vars[name]);
  });
}

/**
 * Resolve a key with its typed slots.
 *
 *   t('home.heading', { clientName })
 *   t('book.heading')
 */
export function t<K extends CopyKey>(key: K, ...vars: SlotArgs<K>): string {
  return resolve(key, (vars[0] ?? {}) as Record<string, SlotValue>);
}

/**
 * Untyped resolution, for the two places a key is only known at runtime: a
 * server refusal's `copyKey` and a constraint's `reasonCopyKey`.
 *
 * A KEY the catalogue does not carry still throws — that surfaces a copy gap in
 * development and in tests rather than rendering nothing. But an unfilled SLOT
 * must NEVER take a client screen down (clarity Finding 1): the crux "you can't
 * have that" copy is exactly the case that must stay clear, and it would
 * otherwise crash the whole configurator when the evaluator assigns a value the
 * generic constraint key with no reason to fill it. So a slot the caller could
 * not supply DEGRADES, it does not throw — to the key's authored slotless twin
 * (`<key>.generic`, e.g. `error.CONSTRAINT_VIOLATION.generic`) when one exists,
 * else to the sentence with the empty slot dropped and its spacing healed. The
 * render site is expected to choose the slotless key up front (ux-writer's rule:
 * use `.generic` whenever a slot is absent); this is the last line of defence so
 * no payload can throw.
 */
export function tDynamic(key: string, vars: Record<string, SlotValue> = {}): string {
  const template = STRINGS[key];
  if (template === undefined) {
    throw new Error(
      `Missing copy key "${key}". Add it to design/copy*.json and rebuild — never inline the string.`,
    );
  }
  let unfilled = false;
  const filled = template.replace(/\{(\w+)\}/g, (_match, name: string) => {
    if (Object.prototype.hasOwnProperty.call(vars, name)) return String(vars[name]);
    unfilled = true;
    return '';
  });
  if (!unfilled) return filled;
  const twin = STRINGS[`${key}.generic`];
  if (typeof twin === 'string') return twin;
  return filled.replace(/\s{2,}/g, ' ').trim();
}

/** Kept for the scaffold's callers and the honesty greps in tests/api. */
export function copy(key: string, vars: Record<string, SlotValue> = {}): string {
  const template = STRINGS[key];
  if (template === undefined) {
    throw new Error(`Missing copy key "${key}".`);
  }
  return template.replace(/\{(\w+)\}/g, (_m, name: string) =>
    Object.prototype.hasOwnProperty.call(vars, name) ? String(vars[name]) : `{${name}}`,
  );
}

export function hasCopy(key: string): boolean {
  return Object.prototype.hasOwnProperty.call(STRINGS, key);
}

export const COPY_KEYS: readonly string[] = COPY_KEY_LIST as readonly string[];
